import React, { useState } from "react";
import { AddNewImageHotel } from "../../../utils/api";

const AddNewImages = ({ hotelCode }) => {
  const [images, setImages] = useState([]);
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [message, setMessage] = useState(""); // Message for success/error
  const [error, setError] = useState(""); // Error message for validation

  const categories = ["Hotel", "Resort", "Hostel", "Villa"];
  const subCategories = {
    Hotel: ["Luxury", "Budget", "Specialty Lodging", "Business"],
    Resort: ["Seaside", "Mountain", "Countryside", "City"],
    Hostel: ["Budget", "Party", "Eco-friendly", "Boutique"],
    Villa: ["Luxury", "Private", "Poolside", "Beachfront"],
  };

  // Handle file selection with validation
  const handleFileChange = (event) => {
    setMessage("");
    setError("");

    const files = Array.from(event.target.files);
    const validFiles = [];
    const maxSize = 1 * 1024 * 1024; // 1MB in bytes

    files.forEach((file) => {
      if (file.size > maxSize) {
        setError("File size should not exceed 1MB");
      } else {
        validFiles.push({
          id: Date.now() + Math.random(),
          file,
          url: URL.createObjectURL(file),
        });
      }
    });

    if (validFiles.length > 0) {
      setImages((prev) => [...prev, ...validFiles]);
    }
  };

  // Handle drag and drop with validation
  const handleDrop = (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    const files = Array.from(event.dataTransfer.files);
    const validFiles = [];
    const maxSize = 1 * 1024 * 1024; // 1MB in bytes

    files.forEach((file) => {
      if (file.size > maxSize) {
        setError("File size should not exceed 1MB");
      } else {
        validFiles.push({
          id: Date.now() + Math.random(),
          file,
          url: URL.createObjectURL(file),
        });
      }
    });

    if (validFiles.length > 0) {
      setImages((prev) => [...prev, ...validFiles]);
    }
  };

  const handleDelete = (id) => {
    setImages((prev) => prev.filter((image) => image.id !== id));
  };

  const preventDefault = (e) => e.preventDefault();

  const handleAddImage = () => {
    if (images.length === 0) {
      setError("Please upload at least one image");
      return;
    }
    if (!category || !subCategory) {
      setError("Please select a category and subcategory");
      return;
    }

    const formData = new FormData();
    images.forEach((image) => {
      formData.append("images", image.file);
    });

    const request = {
      hotelCode,
      category,
      subCategory,
      imageResolution: "ALL",
    };

    formData.append("request", JSON.stringify(request));

    // Make API call
    AddNewImageHotel(formData)
      .then((res) => {
        setMessage({type:"success",message:"Image uploaded successfully!"});
        setError("");
        setImages([]); // Clear uploaded images
      })
      .catch((error) => {
        setError("Error uploading images. Please try again.");
        console.error("Error uploading images:", error);
      });
  };

  return (
    <div className="add-new-images">

      <h2>Add New Images</h2>

      {message && <p className="success-message">{message}</p>}
      {error && <p className="error-message">{error}</p>}

      <div className="dropdowns">
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setSubCategory("");
          }}
        >
          <option value="">Select Category</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <select
          value={subCategory}
          onChange={(e) => setSubCategory(e.target.value)}
          disabled={!category}
        >
          <option value="">Select Subcategory</option>
          {category &&
            subCategories[category]?.map((sub) => (
              <option key={sub} value={sub}>
                {sub}
              </option>
            ))}
        </select>
      </div>

      <div
        className="upload-area"
        onDrop={handleDrop}
        onDragOver={preventDefault}
        onDragEnter={preventDefault}
        onDragLeave={preventDefault}
      >
        <p>Drag an image here</p>
        <input
          type="file"
          multiple
          accept=".jpg, .jpeg, .png, .webp, .avi"
          id="fileUpload"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <label htmlFor="fileUpload" className="file-picker">
          Choose an image to upload
        </label>
        <p>Max file size of 1MB per file (.jpg, .png, .webp, .avi)</p>
      </div>

      <div className="image-gallery">
        {images.map((image) => (
          <div key={image.id} className="thumbnail">
            <img src={image.url} alt="Uploaded" />
            <button onClick={() => handleDelete(image.id)} className="delete-btn">
              &times;
            </button>
          </div>
        ))}
      </div>

      <button className="submit-btn" onClick={handleAddImage}>
        Submit
      </button>

      <style jsx>{`
        .add-new-images {
          padding: 16px;
        }
        .dropdowns {
          margin-bottom: 16px;
        }
        select {
          padding: 6px;
          border-radius: 4px;
          border: 1px solid #ccc;
          margin-right: 8px;
        }
        .upload-area {
          border: 2px dashed #ccc;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
          margin-bottom: 16px;
        }
        .file-picker {
          display: inline-block;
          margin-top: 10px;
          padding: 8px 16px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }
        .file-picker:hover {
          background: #0056b3;
        }
        .image-gallery {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
        }
        .thumbnail {
          position: relative;
          width: 150px;
          border: 1px solid #ccc;
          border-radius: 8px;
          overflow: hidden;
          padding: 8px;
        }
        .thumbnail img {
          width: 100%;
          height: 100px;
          object-fit: cover;
        }
        .delete-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(255, 0, 0, 0.8);
          color: white;
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
        }
        .success-message {
          color: green;
          font-weight: bold;
        }
        .error-message {
          color: red;
          font-weight: bold;
        }
        .submit-btn {
          padding: 10px 16px;
          background: #28a745;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }
        .submit-btn:hover {
          background: #218838;
        }
      `}</style>
    </div>
  );
};

export default AddNewImages;
